import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { PROTOCOL_STEPS } from "@/data/content";
import PageHero from "@/components/arc/PageHero";
import Reveal from "@/components/arc/Reveal";
import { GlowButton, Panel, SimTag } from "@/components/arc/ui";
import { GlowOrbs } from "@/components/arc/Backdrops";

const Protocol = () => {
  const [selected, setSelected] = useState(PROTOCOL_STEPS[0]);

  return (
    <>
      <PageHero
        index="03"
        eyebrow="PROTOCOL"
        title="THE MACHINE PROTOCOL"
        sub="The conceptual architecture of an economic machine: six linked systems, from identity to reputation. Select a stage to inspect it."
      />
      <section className="relative overflow-hidden pb-24 md:pb-36">
        <GlowOrbs />
        <div className="relative mx-auto grid max-w-[1600px] gap-12 px-5 md:px-8 lg:grid-cols-[1fr_1.1fr]">
          <div data-testid="protocol-diagram" className="relative">
            {PROTOCOL_STEPS.map((step, i) => (
              <div key={step.id}>
                <Reveal delay={i * 0.06}>
                  <button
                    type="button"
                    data-testid={`protocol-node-${step.key}`}
                    onClick={() => setSelected(step)}
                    aria-pressed={selected.id === step.id}
                    className={`clip-angular-sm group flex w-full items-center gap-5 border px-6 py-5 text-left transition-all duration-300 ${
                      selected.id === step.id
                        ? "border-electric bg-arc/20 shadow-[0_0_30px_rgba(0,168,255,0.3)]"
                        : "border-ice/15 bg-space/60 hover:border-electric/50"
                    }`}
                  >
                    <span className="font-term text-xs tracking-[0.3em] text-electric">{step.id}</span>
                    <span
                      className={`font-display text-sm font-bold tracking-[0.2em] md:text-base ${
                        selected.id === step.id ? "text-ice" : "text-chrome group-hover:text-ice"
                      }`}
                    >
                      {step.title}
                    </span>
                    <span className="ml-auto font-term text-[10px] tracking-[0.24em] text-silver/50">
                      {selected.id === step.id ? "INSPECTING" : "SELECT"}
                    </span>
                  </button>
                </Reveal>
                {i < PROTOCOL_STEPS.length - 1 && (
                  <div className="flex justify-start py-1 pl-10" aria-hidden="true">
                    <ArrowDown className="h-4 w-4 text-electric/70" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="lg:sticky lg:top-28 lg:self-start">
            <AnimatePresence mode="wait">
              <motion.div
                key={selected.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35 }}
              >
                <Panel className="p-8 md:p-10" data-testid="protocol-detail-panel">
                  <div className="flex items-center justify-between">
                    <span className="font-term text-[11px] tracking-[0.4em] text-electric">
                      STAGE {selected.id}
                    </span>
                    <SimTag>CONCEPT</SimTag>
                  </div>
                  <h2 className="mt-6 font-display text-2xl font-black tracking-[0.1em] text-chrome md:text-3xl" data-testid="protocol-detail-title">
                    {selected.title}
                  </h2>
                  <div className="glow-line mt-6 w-24" />
                  <p className="mt-6 text-base leading-relaxed text-silver md:text-lg" data-testid="protocol-detail-body">
                    {selected.body}
                  </p>
                  <div className="mt-8 border-t border-ice/10 pt-5">
                    <p className="font-term text-[10px] leading-relaxed tracking-[0.2em] text-silver/70">
                      CONCEPTUAL ARCHITECTURE — NO LIVE ON-CHAIN SYSTEM IS CLAIMED FOR THIS STAGE.
                    </p>
                  </div>
                </Panel>
              </motion.div>
            </AnimatePresence>
            <Reveal delay={0.1}>
              <div className="mt-8 flex flex-wrap gap-4">
                <GlowButton to="/identity" testId="protocol-identity-btn">SEE MACHINE IDENTITY</GlowButton>
                <GlowButton to="/docs" variant="ghost" testId="protocol-docs-btn">READ THE DOCS</GlowButton>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
};

export default Protocol;
